import { normalizeName } from "@/utils/normalizeName"

export interface RelationshipChain {
  relationshipNames: string[]
  types: string[]
}

interface RelationshipEdge {
  relationshipName: string
  fromType: string
  toType: string
  normalizedFromType: string
  normalizedToType: string
}

export function relationshipEndpoints(relationshipName: string): { fromType: string; toType: string } | null {
  const parts = relationshipName.split("::")
  if (parts.length !== 2) {
    return null
  }

  const fromType = parts[0]?.trim() ?? ""
  const toType = parts[1]?.trim() ?? ""
  if (!fromType || !toType) {
    return null
  }

  return { fromType, toType }
}

function toRelationshipEdge(relationshipName: string): RelationshipEdge | null {
  const endpoints = relationshipEndpoints(relationshipName)
  if (!endpoints) {
    return null
  }

  const normalizedFromType = normalizeName(endpoints.fromType)
  const normalizedToType = normalizeName(endpoints.toType)
  if (!normalizedFromType || !normalizedToType) {
    return null
  }

  return {
    relationshipName: relationshipName.trim(),
    fromType: endpoints.fromType,
    toType: endpoints.toType,
    normalizedFromType,
    normalizedToType,
  }
}

export function findRelationshipChain(
  relationshipNames: Iterable<string>,
  fromType: string,
  toType: string
): RelationshipChain | null {
  const trimmedFromType = fromType.trim()
  const trimmedToType = toType.trim()
  const normalizedFromType = normalizeName(trimmedFromType)
  const normalizedToType = normalizeName(trimmedToType)

  if (!normalizedFromType || !normalizedToType) {
    return null
  }

  if (normalizedFromType === normalizedToType) {
    return {
      relationshipNames: [],
      types: [trimmedFromType],
    }
  }

  const adjacency = new Map<string, RelationshipEdge[]>()
  const seenEdges = new Set<string>()

  for (const relationshipName of relationshipNames) {
    const edge = toRelationshipEdge(relationshipName)
    if (!edge) {
      continue
    }

    const edgeKey = `${edge.normalizedFromType}|${edge.normalizedToType}`
    if (seenEdges.has(edgeKey)) {
      continue
    }

    seenEdges.add(edgeKey)
    const edges = adjacency.get(edge.normalizedFromType) ?? []
    edges.push(edge)
    adjacency.set(edge.normalizedFromType, edges)
  }

  const queue: Array<{
    normalizedType: string
    relationshipPath: string[]
    typePath: string[]
  }> = [{
    normalizedType: normalizedFromType,
    relationshipPath: [],
    typePath: [trimmedFromType],
  }]
  const visited = new Set<string>([normalizedFromType])

  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) {
      break
    }

    if (current.normalizedType === normalizedToType) {
      return {
        relationshipNames: current.relationshipPath,
        types: current.typePath,
      }
    }

    const nextEdges = adjacency.get(current.normalizedType) ?? []
    for (const edge of nextEdges) {
      if (visited.has(edge.normalizedToType)) {
        continue
      }

      visited.add(edge.normalizedToType)
      queue.push({
        normalizedType: edge.normalizedToType,
        relationshipPath: [...current.relationshipPath, edge.relationshipName],
        typePath: [...current.typePath, edge.toType],
      })
    }
  }

  return null
}
